<template>
  <v-dialog :model-value="modelValue" max-width="560" persistent
    @update:model-value="$emit('update:modelValue', $event)">
    <div class="detail-modal na-modal">
      <header class="na-header">
        <div class="na-heading">
          <div class="form-eyebrow">New asset</div>
          <h2 class="detail-title">Create an asset</h2>
        </div>
        <button type="button" class="na-close" aria-label="Close" @click="close">
          <v-icon size="20">mdi-close</v-icon>
        </button>
      </header>

      <p class="na-sub">
        A site, unit or piece of equipment you walk down. Once it's created you can upload footage
        and Duallo builds the 3D model from it.
      </p>

      <v-form ref="form" @submit.prevent="submit">
        <v-text-field v-model="name" label="Name" placeholder="e.g. Tank farm – Unit 3"
          variant="outlined" density="comfortable" hide-details="auto" autofocus
          :rules="[v => !!(v && v.trim()) || 'Give the asset a name']"
          @keyup.enter="submit" />

        <v-select v-if="sites.length" v-model="siteId" label="Site" :items="siteItems"
          item-title="title" item-value="id" variant="outlined" density="comfortable"
          hide-details="auto" clearable placeholder="No site" class="na-field" />

        <v-textarea v-model="description" label="Description" placeholder="What is it, where is it, anything the team should know"
          variant="outlined" density="comfortable" hide-details="auto" rows="3" auto-grow
          class="na-field" />
      </v-form>

      <p v-if="errorMsg" class="na-error">{{ errorMsg }}</p>

      <footer class="na-footer">
        <span class="na-note">You'll add footage after the asset is created.</span>
        <div class="na-actions">
          <button type="button" class="text-btn" :disabled="saving" @click="close">Cancel</button>
          <button type="button" class="primary-btn" :disabled="!canCreate || saving" @click="submit">
            <v-progress-circular v-if="saving" indeterminate size="18" width="2" />
            <span v-else>Create asset</span>
          </button>
        </div>
      </footer>
    </div>
  </v-dialog>
</template>

<script>
import CreateAssetModel from '@/domain/asset/CreateAssetModel'
import AssetService from '@/services/AssetService'

export default {
  name: 'NewAssetDialog',
  props: {
    modelValue: { type: Boolean, default: false },
    organizationId: { type: [String, Number], default: null },
    sites: { type: Array, default: () => [] },
  },
  emits: ['update:modelValue', 'created'],
  data() {
    return {
      name: '',
      description: '',
      siteId: null,
      saving: false,
      errorMsg: '',
    }
  },
  computed: {
    siteItems() {
      // Normalise to { id, title } so either raw sites or the home page's mapped list work.
      return this.sites.map(s => ({ id: s.id, title: s.title ?? s.name }))
    },
    canCreate() {
      return !!(this.name && this.name.trim() && this.organizationId)
    },
  },
  watch: {
    // Reset the form each time the dialog opens so a previous entry never lingers.
    modelValue(open) {
      if (open) this.reset()
    },
  },
  methods: {
    reset() {
      this.name = ''
      this.description = ''
      this.siteId = null
      this.saving = false
      this.errorMsg = ''
    },
    close() {
      if (this.saving) return
      this.$emit('update:modelValue', false)
    },
    async submit() {
      if (!this.canCreate || this.saving) return
      this.errorMsg = ''
      this.saving = true
      try {
        const model = new CreateAssetModel(
          this.name.trim(),
          this.description?.trim() || null,
          this.organizationId,
          this.siteId || null,
        )
        const asset = await AssetService.createAssetAsync(model)
        if (asset?.id) {
          this.$emit('created', asset)
          this.$emit('update:modelValue', false)
        } else {
          this.errorMsg = 'The asset could not be created. Please try again.'
        }
      } catch {
        this.errorMsg = 'Something went wrong creating the asset. Please try again.'
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.na-modal {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  color: #fff;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
}

.form-eyebrow {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--duallo-accent);
}
.detail-title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
}

.text-btn {
  padding: 10px 18px;
  border-radius: 10px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #cfd8dc;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}
.text-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.primary-btn {
  padding: 10px 22px;
  border-radius: 10px;
  background: var(--duallo-accent-gradient);
  color: #071514;
  font-weight: 700;
  border: none;
  font: inherit;
  cursor: pointer;
  min-width: 130px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.primary-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.na-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.na-heading { display: flex; flex-direction: column; gap: 4px; }

.na-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* p5-4: >=44px touch target (icon stays visually 20px, centered by flex) */
  min-width: 44px;
  min-height: 44px;
  padding: 0;
  border-radius: 9px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #b0bec5;
  cursor: pointer;
  flex: 0 0 auto;
}
.na-close:hover { background: rgba(255, 255, 255, 0.06); color: #fff; }

.na-sub {
  margin: -2px 0 4px;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #9db0ba;
}

.na-field { margin-top: 14px; }

.na-error {
  margin: 0;
  font-size: 0.86rem;
  color: #ff8a80;
}

.na-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  padding-top: 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}
.na-note { font-size: 0.8rem; color: #7f939d; }
.na-actions { display: flex; gap: 10px; align-items: center; }
</style>
